/**
 * Centralized SEO configuration for all public pages.
 *
 * Each entry follows the same shape:
 *   - title:       { fr, en } — 50-60 chars, include primary keyword
 *   - description: { fr, en } — 150-160 chars, value prop + CTA
 *   - path:        canonical path (without domain)
 *   - image:       (optional) page-specific OG image, falls back to og-image.jpg
 */

const OG_DEFAULT = '/og-image.png';

export const SEO = {
  shop: {
    title: {
      fr: 'Boutique - Matériel géotechnique à louer ou acheter',
      en: 'Shop - Geotechnical equipment for rent or sale',
    },
    description: {
      fr: 'Catalogue complet de foreuses, pompes à béton, pressiomètres et matériel de fondations spéciales. Achat, location, occasion - Europe & Afrique.',
      en: 'Full catalog of drilling rigs, concrete pumps, pressuremeters and special foundations equipment. Buy, rent, used - Europe & Africa.',
    },
    path: '/shop',
    image: '/og-image-shop.jpg',
  },

  suppliers: {
    title: {
      fr: 'Fournisseurs vérifiés - Géotechnique & fondations',
      en: 'Verified suppliers - Geotechnical & foundations',
    },
    description: {
      fr: 'Annuaire des fournisseurs vérifiés en matériel géotechnique. Fabricants, distributeurs, loueurs partout en Europe et en Afrique.',
      en: 'Directory of verified geotechnical equipment suppliers. Manufacturers, distributors, rental companies across Europe and Africa.',
    },
    path: '/suppliers',
    image: '/og-image-suppliers.jpg',
  },

  directory: {
    title: {
      fr: 'Annuaire pro - Bureaux d\'études & entreprises BTP',
      en: 'Pro directory - Engineering firms & BTP companies',
    },
    description: {
      fr: 'Trouvez un bureau d\'études géotechnique, une entreprise de fondations spéciales ou un expert près de chez vous. Annuaire B2B vérifié.',
      en: 'Find a geotechnical engineering firm, special foundations contractor or expert near you. Verified B2B directory.',
    },
    path: '/directory',
    image: '/og-image-suppliers.jpg',
  },

  requests: {
    title: {
      fr: 'Demandes de matériel - Marketplace B2B géotechnique',
      en: 'Equipment requests - B2B geotechnical marketplace',
    },
    description: {
      fr: 'Consultez les demandes de matériel et services géotechniques publiées par les professionnels. Répondez et remportez de nouveaux chantiers.',
      en: 'Browse geotechnical equipment and service requests posted by professionals. Reply and win new projects.',
    },
    path: '/requests',
    image: OG_DEFAULT,
  },

  talents: {
    title: {
      fr: 'Talents BTP - Recrutez foreurs, hydrauliciens, techniciens',
      en: 'BTP Talents - Hire drillers, hydraulics techs, operators',
    },
    description: {
      fr: 'Recrutez des opérateurs de foreuse, hydrauliciens et techniciens qualifiés en géotechnique pour vos chantiers en Europe et en Afrique.',
      en: 'Hire drilling operators, hydraulics technicians and skilled geotechnical workers for your sites across Europe and Africa.',
    },
    path: '/talents',
    image: '/og-image-talents.jpg',
  },

  comparator: {
    title: {
      fr: 'Comparateur de devis IA - Matériel géotechnique',
      en: 'AI quote comparator - Geotechnical equipment',
    },
    description: {
      fr: 'Comparez automatiquement vos devis fournisseurs grâce à notre IA. Gain de temps, transparence des prix, meilleure décision d\'achat.',
      en: 'Automatically compare supplier quotes with our AI. Save time, get price transparency, make better buying decisions.',
    },
    path: '/comparateur',
    image: OG_DEFAULT,
  },

  pricing: {
    title: {
      fr: 'Tarifs et abonnements - GEOTS Consulting',
      en: 'Pricing and subscriptions - GEOTS Consulting',
    },
    description: {
      fr: 'Découvrez nos formules d\'abonnement pour fournisseurs et acheteurs. Plans flexibles adaptés aux pros de la géotechnique et du BTP.',
      en: 'Discover our subscription plans for suppliers and buyers. Flexible plans tailored to geotechnical and BTP professionals.',
    },
    path: '/pricing',
    image: '/og-image-pricing.jpg',
  },

  // ── NEW: How it works ──
  howItWorks: {
    title: {
      fr: 'Comment ça marche - Guide GEOTS Consulting',
      en: 'How it works - GEOTS Consulting Guide',
    },
    description: {
      fr: 'Découvrez comment GEOTS Consulting connecte fournisseurs et acheteurs de matériel géotechnique entre l\'Europe et l\'Afrique. Inscription gratuite, vérification, mise en relation.',
      en: 'Discover how GEOTS Consulting connects geotechnical equipment suppliers and buyers across Europe and Africa. Free signup, verification, B2B matching.',
    },
    path: '/comment-ca-marche',
    image: OG_DEFAULT,
  },

  // Legal pages
  legalNotice: {
    title: { fr: 'Mentions légales', en: 'Legal notice' },
    description: {
      fr: 'Mentions légales du site geots.consulting - Marketplace B2B géotechnique.',
      en: 'Legal notice for geots.consulting - B2B geotechnical marketplace.',
    },
    path: '/legal-notice',
    image: OG_DEFAULT,
  },

  termsOfService: {
    title: { fr: 'Conditions générales d\'utilisation', en: 'Terms of service' },
    description: {
      fr: 'Conditions générales d\'utilisation de la plateforme GEOTS Consulting. Règles, droits et obligations des utilisateurs.',
      en: 'Terms of service for the GEOTS Consulting platform. Rules, rights and obligations of users.',
    },
    path: '/terms-of-service',
    image: OG_DEFAULT,
  },

  privacyPolicy: {
    title: { fr: 'Politique de confidentialité', en: 'Privacy policy' },
    description: {
      fr: 'Politique de confidentialité GEOTS Consulting. Comment nous collectons, utilisons et protégeons vos données personnelles (RGPD).',
      en: 'GEOTS Consulting privacy policy. How we collect, use and protect your personal data (GDPR compliant).',
    },
    path: '/privacy-policy',
    image: OG_DEFAULT,
  },

  cookiePolicy: {
    title: { fr: 'Politique de cookies', en: 'Cookie policy' },
    description: {
      fr: 'Comment GEOTS Consulting utilise les cookies. Liste complète, finalités et durée de conservation conformes au RGPD.',
      en: 'How GEOTS Consulting uses cookies. Full list, purposes and retention periods compliant with GDPR.',
    },
    path: '/cookie-policy',
    image: OG_DEFAULT,
  },

  // Authentication pages: NOINDEX
  login: {
    title: { fr: 'Connexion', en: 'Login' },
    description: {
      fr: 'Connectez-vous à votre espace GEOTS Consulting.',
      en: 'Log in to your GEOTS Consulting account.',
    },
    path: '/login',
    image: OG_DEFAULT,
    noindex: true,
  },

  register: {
    title: { fr: 'Créer un compte', en: 'Sign up' },
    description: {
      fr: 'Créez votre compte gratuit sur GEOTS Consulting et accédez à la marketplace géotechnique.',
      en: 'Create your free GEOTS Consulting account and access the geotechnical marketplace.',
    },
    path: '/register',
    image: OG_DEFAULT,
    noindex: true,
  },

  forgotPassword: {
    title: { fr: 'Mot de passe oublié', en: 'Forgot password' },
    description: { fr: 'Réinitialisez votre mot de passe.', en: 'Reset your password.' },
    path: '/forgot-password',
    image: OG_DEFAULT,
    noindex: true,
  },

  stores: {
    title: {
      fr: 'Boutiques fournisseurs - Marketplace géotechnique',
      en: 'Supplier stores - Geotechnical marketplace',
    },
    description: {
      fr: 'Parcourez les boutiques de nos fournisseurs vérifiés en matériel géotechnique et fondations spéciales.',
      en: 'Browse our verified geotechnical and special foundations suppliers stores.',
    },
    path: '/stores',
    image: '/og-image-suppliers.jpg',
  },

  rental: {
    title: {
      fr: 'Location de matériel géotechnique - Foreuses, pompes',
      en: 'Geotechnical equipment rental - Drills, pumps',
    },
    description: {
      fr: 'Louez du matériel géotechnique courte ou longue durée avec livraison sur chantier en Europe et en Afrique.',
      en: 'Rent geotechnical equipment short or long-term with on-site delivery across Europe and Africa.',
    },
    path: '/rental',
    image: '/og-image-shop.jpg',
  },

  guideForeuseChoice: {
    title: {
      fr: 'Comment choisir une foreuse géotechnique ? Guide complet 2026',
      en: 'How to choose a geotechnical drilling rig? Complete guide 2026',
    },
    description: {
      fr: 'Guide expert pour choisir votre foreuse géotechnique : 10 critères, marques (Comacchio, Soilmec, Bauer), neuf/occasion, prix. Conseils de professionnels.',
      en: 'Expert guide to choosing your geotechnical drilling rig: 10 criteria, brands (Comacchio, Soilmec, Bauer), new/used, prices. Professional advice.',
    },
    path: '/guides/comment-choisir-foreuse-geotechnique',
    image: '/og-image-guide-foreuse.jpg',
  },

  guideEtalonnageCapteurs: {
    title: {
      fr: 'Étalonnage des capteurs de forage : obligations, normes et bonnes pratiques | Guide',
      en: 'Drilling sensor calibration: requirements, standards and best practices | Guide',
    },
    description: {
      fr: 'Étalonnage des capteurs de forage et d\'instrumentation géotechnique : obligations, norme ISO 17025, traçabilité métrologique, fréquence et certificat. Le guide complet.',
      en: 'Calibration of drilling sensors and geotechnical instrumentation: requirements, ISO 17025 standard, metrological traceability, frequency and certificate. The complete guide.',
    },
    path: '/guides/etalonnage-capteurs-forage',
    image: '/og-image-guide-etalonnage.jpg',
  },

  about: {
    title: {
      fr: 'À propos de GEOTS Consulting | Marketplace géotechnique',
      en: 'About GEOTS Consulting | Geotechnical Marketplace',
    },
    description: {
      fr: 'Hamdani Ali, technicien spécialisé en instrumentation forage avec 15 ans d\'expérience, présente GEOTS Consulting, la marketplace 100% géotechnique entre France et Afrique francophone.',
      en: 'Hamdani Ali, drilling instrumentation specialist with 15 years of experience, presents GEOTS Consulting, the 100% geotechnical marketplace between France and French-speaking Africa.',
    },
    path: '/a-propos',
    image: '/og-image.jpg',
  },
};

export default SEO;
